<template>
    <div id="ob-shell" :class="{ 'ob-corp': store.isCorp }">
        <AuroraBlooms />
        <WaveBackdrop />
        <SectionRail />

        <div id="ob-content">
            <NetworkNotice />
            <TopNav />
            <HeroSection />
            <TechnologySection />
            <OfferSection />
            <AboutSection />
            <LoyaltySection />
            <FaqSection />
            <ContactSection />
            <FooterSection />
            <SpeedStrip />
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { nextTick, onBeforeUnmount, onMounted, watch } from 'vue'

    import { buildWaveMask, setupWaveDraw } from '@/libs/wave'
    import { useLandingStore } from '@/stores/landing'
    import AboutSection from '@/views/landing/components/AboutSection.vue'
    import AuroraBlooms from '@/views/landing/components/AuroraBlooms.vue'
    import ContactSection from '@/views/landing/components/ContactSection.vue'
    import FaqSection from '@/views/landing/components/FaqSection.vue'
    import FooterSection from '@/views/landing/components/FooterSection.vue'
    import HeroSection from '@/views/landing/components/HeroSection.vue'
    import LoyaltySection from '@/views/landing/components/LoyaltySection.vue'
    import NetworkNotice from '@/views/landing/components/NetworkNotice.vue'
    import OfferSection from '@/views/landing/components/OfferSection.vue'
    import SectionRail from '@/views/landing/components/SectionRail.vue'
    import SpeedStrip from '@/views/landing/components/SpeedStrip.vue'
    import TechnologySection from '@/views/landing/components/TechnologySection.vue'
    import TopNav from '@/views/landing/components/TopNav.vue'
    import WaveBackdrop from '@/views/landing/components/WaveBackdrop.vue'

    const store = useLandingStore()

    let maskRaf = 0
    const scheduleMask = () => {
        if (maskRaf) cancelAnimationFrame(maskRaf)
        maskRaf = requestAnimationFrame(() => { maskRaf = 0; buildWaveMask() })
    }

    let cleanupWave: (() => void) | null = null
    let bloomRaf = 0
    let io: IntersectionObserver | null = null
    const timers: ReturnType<typeof setTimeout>[] = []

    const onBloomScroll = () => {
        const el = document.getElementById('ob-blooms')
        if (!el || bloomRaf) return
        bloomRaf = requestAnimationFrame(() => {
            bloomRaf = 0
            el.style.transform = 'translateY(' + ((window.scrollY || 0) * -0.05).toFixed(1) + 'px)'
        })
    }

    // Any state change that reflows the page (lane switch, accordions, tier rows)
    // moves text, so the wave clearance mask is rebuilt after the DOM settles.
    watch(
        () => [store.mode, store.bearer, store.layer, store.workStep, store.engageStep, store.tier, store.advance, store.klass, store.faq],
        async () => {
            await nextTick()
            scheduleMask()
        }
    )

    onMounted(() => {
        const reduce = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches)

        scheduleMask()
        timers.push(setTimeout(scheduleMask, 400), setTimeout(scheduleMask, 1400), setTimeout(scheduleMask, 2600))
        window.addEventListener('resize', scheduleMask)
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(scheduleMask).catch(() => {})

        if (!reduce) {
            cleanupWave = setupWaveDraw()
            window.addEventListener('scroll', onBloomScroll, { passive: true })
            store.startSlideshow()

            // Sections fade/rise in once
            if ('IntersectionObserver' in window) {
                const items = Array.from(document.querySelectorAll('#ob-content > section, #ob-content > header, #ob-content > footer'))
                if (items.length) {
                    io = new IntersectionObserver((es) => {
                        es.forEach((e) => {
                            if (e.isIntersecting) {
                                e.target.classList.add('ob-rv')
                                io?.unobserve(e.target)
                            }
                        })
                    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.04 })
                    items.forEach((el) => io?.observe(el))
                }
            }
        }
    })

    onBeforeUnmount(() => {
        window.removeEventListener('resize', scheduleMask)
        window.removeEventListener('scroll', onBloomScroll)
        if (cleanupWave) cleanupWave()
        if (maskRaf) cancelAnimationFrame(maskRaf)
        if (bloomRaf) cancelAnimationFrame(bloomRaf)
        timers.forEach((t) => clearTimeout(t))
        io?.disconnect()
        store.stopSlideshow()
    })
</script>

<style scoped>
    #ob-shell {
        position: relative;
        background: var(--ob-canvas);
        color: var(--ob-ink);
        font-family: 'Poppins', sans-serif;
        /* clip, not hidden: hidden computes overflow-y to auto and iOS then
           treats the shell as a scroll container, un-pinning fixed children */
        overflow-x: clip;
    }
    #ob-content { position: relative; z-index: 1; }
</style>
